import { useState } from "react";
import { api, userAvatarUrl, type ChatReminder } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { DevaAvatar } from "@/components/Deva";
import { Icon } from "@/components/Icon";
import { ClockIcon } from "./ScheduleSendMenu";
import { ReminderDialog, ReminderEditor } from "./ReminderEditor";
import {
  editPatch,
  fmtDue,
  fmtStamp,
  reminderErrorText,
  reminderStale,
  reminderView,
  snoozeDue,
} from "./reminders";
import "./reminders.css";

// Карточка напоминания в ленте — под плашкой звонка, из которого LLM его
// предложила (Conversation подвешивает её тем же приёмом, что ReferenceBlock
// под сообщением). Вид — как всплывашка после звонка: белая карточка с синей
// полосой слева, аватар и заголовок, маленькая синяя подпись, текст, срок,
// основание из разговора и ряд кнопок-пилюль.
//
// Состояния: предложение (Принять · Изменить · Не нужно) → «Изменить»
// раскрывает форму на месте; активное (Изменить · Сделано · Удалить) — в том
// числе поставленное автоматически, без «Принять» (настройка «Напоминания из
// звонков»); наступившее (Сделано · Через час · Изменить). Закрытое — выполнено,
// отклонено, удалено — сжимается в одну тихую строку: решение принято, а
// место в ленте нужно переписке.


/** Одно действие с напоминанием: занятость, ошибка по-русски и перечитка.
 *  Отказ «уже поменяли» (409/404) тоже перечитывает — карточка покажет, что
 *  с ней стало, вместо кнопок, которые больше ничего не сделают. Общая для
 *  карточки, полосы над композером и всплывашки. */
export function useReminderAction(onChanged: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      return true;
    } catch (e) {
      setError(reminderErrorText(e));
      if (reminderStale(e)) onChanged();
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

/** «Кандидат писал после этого» / «После этого был звонок». Принятое
 *  напоминание само не снимается, но рекрутёр должен знать, что дело могло
 *  решиться без него, — там, где он его делает: в карточке, в полосе над
 *  композером и во всплывашке. Ручное к звонку не привязано и карточки в
 *  ленте не имеет вовсе, так что полоса и всплывашка — его единственные места. */
export function ReminderFlags({
  reminder: r,
  className,
}: {
  reminder: Pick<ChatReminder, "peer_wrote_after" | "called_after">;
  className: string;
}) {
  if (!r.peer_wrote_after && !r.called_after) return null;
  return (
    <div className={className}>
      {r.peer_wrote_after && <span className="rem-flag">Кандидат писал после этого</span>}
      {r.called_after && <span className="rem-flag">После этого был звонок</span>}
    </div>
  );
}

export function ReminderCard({
  reminder: r,
  onChanged,
  standalone = false,
  sheet = false,
  now = Date.now(),
}: {
  reminder: ChatReminder;
  /** что-то поменялось — перечитать напоминания человека */
  onChanged: () => void;
  /** не под плашкой звонка (её нет в загруженной ленте) — обычный отступ сверху */
  standalone?: boolean;
  /** Телефон: «Изменить» — листом снизу (ReminderDialog, закрывается «Назад»),
   *  а не формой в ленте: в узкой ленте под клавиатурой её было бы не видно. */
  sheet?: boolean;
  /** «сейчас» для срока словами; параметр — ради тестов и превью */
  now?: number;
}) {
  const [editing, setEditing] = useState(false);
  const { busy, error, setError, run } = useReminderAction(onChanged);
  const view = reminderView(r, now);
  const nowDate = new Date(now);

  if (view === "superseded") return null;
  if (view === "done" || view === "dismissed" || view === "cancelled") {
    const [what, who] =
      view === "done"
        ? ["Напоминание выполнено", r.resolved_by_name]
        : view === "dismissed"
          ? ["Предложение отклонено", r.decided_by_name]
          : ["Напоминание удалено", r.resolved_by_name];
    const at = (view === "dismissed" ? r.decided_at : r.resolved_at) ?? r.updated_at;
    return (
      <div className={"rem-quiet" + (standalone ? " rem-quiet--standalone" : "")} title={r.text}>
        <Icon name={view === "done" ? "check" : "close"} size={12} strokeWidth={1.8} />
        <span className="rem-quiet__what">
          {what}
          {who ? ` · ${who}` : ""}
        </span>
        <span className="rem-quiet__text">{r.text}</span>
        {at && <span className="rem-quiet__time">{fmtStamp(at, nowDate)}</span>}
      </div>
    );
  }

  const llm = r.source === "llm";
  const proposed = view === "proposed";
  const due = view === "due";
  // Предложение, чей срок уже прошёл (карточку открыли на следующий день):
  // «Принять» как есть сервер отклонит — сразу форма с новым временем.
  const lapsed = proposed && Date.parse(r.due_at) <= now;
  const label = proposed
    ? lapsed
      ? "Предложение — время уже прошло"
      : "Предложение — проверьте время"
    : due
      ? "Пора"
      : r.decision === "auto"
        ? // Никто не нажимал «Принять»: поставила система по настройке, имя —
          // кому оно (звонивший рекрутёр или владелец чата).
          `Поставлено автоматически${r.user_name ? ` · ${r.user_name}` : ""}`
        : llm
          ? `Принято${r.user_name ? ` · ${r.user_name}` : ""}`
          : `Создано${r.created_by_name ? ` · ${r.created_by_name}` : ""}`;
  const author = r.created_by_name ?? "Рекрутёр";
  const authorSrc = r.created_by_user_id ? userAvatarUrl(r.created_by_user_id) : null;
  const ev = r.evidence;
  // Как было сказано время — только когда срок не назван точно: «завтра в 11»
  // и так видно в сроке, а «после обеда» объясняет, откуда взялись 14:00.
  const said = ev?.when_said && ev.time_basis !== "exact" ? ev.when_said : null;

  const accept = () => {
    if (lapsed) {
      setError("Время уже прошло — выберите новое");
      setEditing(true);
      return;
    }
    void run(() => api.chatReminderAccept(r.id));
  };
  // Что уходит на сервер из формы: у предложения — принятие (с правкой или
  // без), у активного — только изменённое; ничего не изменили — ничего.
  const send = (text: string, when: Date): (() => Promise<unknown>) | null => {
    const patch = editPatch(r, text, when);
    if (!proposed && Object.keys(patch).length === 0) return null;
    return () =>
      proposed ? api.chatReminderAccept(r.id, patch) : api.chatReminderUpdate(r.id, patch);
  };
  const save = (text: string, when: Date) => {
    const fn = send(text, when);
    if (!fn) {
      setEditing(false);
      return;
    }
    void run(fn).then((ok) => ok && setEditing(false));
  };
  // Лист снизу: ошибку показывает он сам (onSubmit бросает), карточка лишь
  // перечитывается — и после отказа «уже поменяли» тоже.
  const saveInSheet = async (text: string, when: Date) => {
    const fn = send(text, when);
    try {
      if (fn) await fn();
    } catch (e) {
      if (reminderStale(e)) onChanged();
      throw e;
    }
    setEditing(false);
    setError(null);
    if (fn) onChanged();
  };
  const closeEditor = () => {
    setEditing(false);
    setError(null);
  };
  const remove = () => {
    if (!window.confirm("Удалить напоминание?")) return;
    void run(() => api.chatReminderDelete(r.id));
  };

  return (
    <section
      className={
        "rem-card" +
        (proposed ? " rem-card--proposed" : "") +
        (due ? " rem-card--due" : "") +
        (standalone ? " rem-card--standalone" : "")
      }
      aria-label={llm ? "Напоминание после звонка" : "Напоминание"}
    >
      <div className="rem-card__head">
        {llm ? (
          // Предложила Дева (из разговора) — её аватарка, а не инициалы.
          <DevaAvatar size={22} />
        ) : (
          <Avatar name={author} src={authorSrc} size={22} />
        )}
        <div className="rem-card__hl">
          <div className="rem-card__top">
            <b className="rem-card__title">{llm ? "Напоминание после звонка" : "Напоминание"}</b>
            <span className="rem-card__time" title={new Date(r.created_at).toLocaleString("ru-RU")}>
              {fmtStamp(r.created_at, nowDate)}
            </span>
          </div>
          <div className={"rem-card__label" + (due || lapsed ? " rem-card__label--due" : "")}>
            {label}
          </div>
        </div>
      </div>

      {editing && sheet && (
        <ReminderDialog
          title={llm ? "Напоминание после звонка" : "Напоминание"}
          text={r.text}
          due={lapsed ? null : new Date(r.due_at)}
          submitLabel={proposed ? "Сохранить и принять" : "Сохранить"}
          error={error}
          onSubmit={saveInSheet}
          onClose={closeEditor}
        />
      )}
      {editing && !sheet ? (
        <ReminderEditor
          text={r.text}
          due={lapsed ? null : new Date(r.due_at)}
          submitLabel={proposed ? "Сохранить и принять" : "Сохранить"}
          busy={busy}
          error={error}
          autoFocus
          onSubmit={save}
          onCancel={closeEditor}
        />
      ) : (
        <>
          <div className="rem-card__text">{r.text}</div>
          <div className="rem-card__due">
            <ClockIcon />
            <span>{fmtDue(r.due_at, nowDate)}</span>
            {said && <span className="rem-card__said">· в разговоре: «{said}»</span>}
          </div>
          {ev?.quote && (
            <div className="rem-card__quote" title={ev.reason ?? undefined}>
              Из разговора: «{ev.quote}»
            </div>
          )}
          <ReminderFlags reminder={r} className="rem-card__flags" />
          {error && !(editing && sheet) && (
            <div className="rem-card__err" role="alert">
              {error}
            </div>
          )}
          <div className="rem-card__acts">
            {proposed && (
              <>
                <button type="button" className="rem-btn rem-btn--accent" disabled={busy} onClick={accept}>
                  Принять
                </button>
                <button type="button" className="rem-btn" disabled={busy} onClick={() => setEditing(true)}>
                  Изменить
                </button>
                <button
                  type="button"
                  className="rem-btn"
                  disabled={busy}
                  onClick={() => void run(() => api.chatReminderDismiss(r.id))}
                >
                  Не нужно
                </button>
              </>
            )}
            {view === "active" && (
              <>
                <button type="button" className="rem-btn" disabled={busy} onClick={() => setEditing(true)}>
                  Изменить
                </button>
                <button
                  type="button"
                  className="rem-btn rem-btn--accent"
                  disabled={busy}
                  onClick={() => void run(() => api.chatReminderDone(r.id))}
                >
                  Сделано
                </button>
                <button type="button" className="rem-btn" disabled={busy} onClick={remove}>
                  Удалить
                </button>
              </>
            )}
            {due && (
              <>
                <button
                  type="button"
                  className="rem-btn rem-btn--accent"
                  disabled={busy}
                  onClick={() => void run(() => api.chatReminderDone(r.id))}
                >
                  Сделано
                </button>
                <button
                  type="button"
                  className="rem-btn"
                  disabled={busy}
                  onClick={() => void run(() => api.chatReminderUpdate(r.id, { due_at: snoozeDue() }))}
                >
                  Через час
                </button>
                <button type="button" className="rem-btn" disabled={busy} onClick={() => setEditing(true)}>
                  Изменить
                </button>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}
